import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import * as routes from '../routes';
import { navigate } from 'gatsby';
import {
  StaffProfileViewType,
  StaffResumeViewType,
} from '../types/services-api';

export const computeProfileCompletion = (props: {
  staffData: StaffProfileViewType;
  resumeProfileData: StaffResumeViewType;
}) => {
  const { staffData, resumeProfileData } = props;
  let profileRequirements = [
    {
      label: 'Add an introduction to yourself',
      handleClick: () => navigate(routes.toResume),
      value: 10,
      isMissing: isMissing(resumeProfileData.intro),
      isRequired: true,
      category: 'resume',
    },
    {
      label: 'Add your skills',
      handleClick: () => navigate(routes.toSkillsAssessment),
      value: 10,
      isMissing: isMissing(resumeProfileData.skills),
      isRequired: true,
      category: 'assessments',
    },
    {
      label: 'Add more skills',
      handleClick: () => navigate(routes.toSkillsAssessment),
      value: 5,
      isMissing:
        !isMissing(resumeProfileData.skills) &&
        isMissingEnoughSkills(resumeProfileData.skills),
      category: 'assessments',
    },
    {
      label: 'Make your resume public',
      handleClick: () => navigate(routes.toResume),
      value: 5,
      isMissing: isMissing(resumeProfileData.isPublic),
      category: 'resume',
    },
    {
      label: 'Add a profile photo',
      handleClick: () => navigate(routes.toResume),
      value: 5,
      isMissing: isMissing(staffData.hasPhoto),
      category: 'resume',
    },
    {
      label: 'Add your availability',
      handleClick: () => navigate(routes.toResume),
      value: 5,
      isMissing: isMissingAvailability(resumeProfileData),
      category: 'resume',
    },
    {
      label: 'Add your license #',
      handleClick: () => navigate(routes.toResume),
      value: 5,
      isMissing: isMissingLicense(
        resumeProfileData.license,
        staffData.position
      ),
      category: 'resume',
    },
    {
      label: 'Add any other achievements',
      handleClick: () => navigate(routes.toResume),
      value: 5,
      isMissing: isMissing(resumeProfileData.other),
      category: 'resume',
    },
    {
      label: 'Specify your years of service',
      handleClick: () => navigate(routes.toResume),
      value: 5,
      isMissing: isMissing(resumeProfileData.experience),
      category: 'resume',
    },
    {
      label: 'Add a salary requirement',
      handleClick: () => navigate(routes.toResume),
      value: 5,
      isMissing: isMissing(resumeProfileData.salary),
      category: 'resume',
    },
    {
      label: 'Take the DISC assessment',
      handleClick: () => navigate(routes.toDiscAssessment),
      value: 5,
      isMissing: isMissing(staffData.discScores),
      category: 'assessments',
    },
    {
      label: 'Take the Emotional Intelligence assessment',
      handleClick: () => navigate(routes.toEmotionalIntelligenceAssessment),
      value: 5,
      isMissing: !staffData.emotion.isComplete,
      category: 'assessments',
    },
    {
      label: 'Take the Core Values assessment',
      handleClick: () => navigate(routes.toCoreValuesAssessment),
      value: 5,
      isMissing: isMissing(staffData.values),
      category: 'assessments',
    },
    {
      label: 'Take the Skills assessment',
      handleClick: () => navigate(routes.toSkillsAssessment),
      value: 5,
      isMissing: isMissingSkillsAssessment(resumeProfileData.skills),
      category: 'assessments',
    },
    {
      label: 'Take the Culture assessment',
      handleClick: () => navigate(routes.toCultureMultipleChoiceAssessment),
      value: 5,
      isMissing: isMissing(staffData.culture),
      category: 'assessments',
    },
    {
      label: 'Take the Alignment Questionnaire',
      handleClick: () => navigate(routes.toAlignmentAssessment),
      value: 5,
      isMissing:
        isMissing(
          _get(staffData, 'alignmentQuestionnaire.personalVisionAnswer', null)
        ) ||
        isMissing(
          _get(staffData, 'alignmentQuestionnaire.teamExpectationsAnswer', null)
        ),
      category: 'assessments',
    },
    {
      label: 'Add your work history',
      handleClick: () => navigate(routes.toResume),
      value: 5,
      isMissing: isMissing(resumeProfileData.companies),
      category: 'resume',
    },
    {
      label: 'Add your education',
      handleClick: () => navigate(routes.toResume),
      value: 5,
      isMissing: isMissing(resumeProfileData.schools),
      category: 'resume',
    },
  ];

  // calculate the percentComplete by deducting points for each missing requirement
  const percentComplete = profileRequirements.reduce((percent, requirement) => {
    if (requirement.isMissing) {
      percent -= requirement.value;
    }
    return percent;
  }, 100);

  return { percentComplete, profileRequirements };
};

export const isMissing = (item) => {
  // first check for false, null, undefined, 0, NaN, empty string
  // note that the string "0" is true (i.e. "0" years of experience)
  if (!item) {
    return true;
  } else if (typeof item === 'string') {
    item = item.toLowerCase();
    return item === 'none specified' || item === 'not specified';
  } else if (Array.isArray(item)) {
    return item.length === 0;
  } else if (typeof item === 'object') {
    return _isEmpty(item);
  }
  // otherwise, the item is not missing
  return false;
};

export const isMissingAvailability = (resume) => {
  return isMissing(resume.dowTotal) && isMissing(resume.dowTotalTemp);
};

export const isMissingLicense = (license, position) => {
  return (position === 'DD' || position === 'DH') && isMissing(license);
};

export const isMissingSkillsAssessment = (skills) => {
  let skillScoreSum = skills?.reduce((acc, curr) => curr.score + acc, 0);
  return skills?.length === 0 || skillScoreSum <= skills?.length;
};

export const isMissingEnoughSkills = (skills) => {
  return skills.length > 1 && skills.length < 6;
};

export const introStepComplete = ({ intro }) => {
  return intro && intro.length;
};

export const availabilityStepComplete = ({ data }) => {
  return availableForTempWork(data) || availableForPermanentWork(data);
};

export const workExperienceComplete = ({ skills, position, license }) => {
  return !isMissingLicense(license, position) && skills.length >= 5;
};

export const availableForTempWork = (values) => {
  return [
    'isDowSunTemp',
    'isDowMonTemp',
    'isDowTueTemp',
    'isDowWedTemp',
    'isDowThurTemp',
    'isDowFriTemp',
    'isDowSatTemp',
  ].some((key) => values[key]);
};

export const availableForPermanentWork = (values) => {
  return [
    'isDowSun',
    'isDowMon',
    'isDowTue',
    'isDowWed',
    'isDowThur',
    'isDowFri',
    'isDowSat',
  ].some((key) => values[key]);
};

export const daysOfWeekPermOptions = [
  {
    code: 'isDowSun',
    label: 'Sun',
  },
  {
    code: 'isDowMon',
    label: 'Mon',
  },
  {
    code: 'isDowTue',
    label: 'Tue',
  },
  {
    code: 'isDowWed',
    label: 'Wed',
  },
  {
    code: 'isDowThur',
    label: 'Thur',
  },
  {
    code: 'isDowFri',
    label: 'Fri',
  },
  {
    code: 'isDowSat',
    label: 'Sat',
  },
];

export const daysOfWeekTempOptions = [
  {
    code: 'isDowSunTemp',
    label: 'Sun',
  },
  {
    code: 'isDowMonTemp',
    label: 'Mon',
  },
  {
    code: 'isDowTueTemp',
    label: 'Tue',
  },
  {
    code: 'isDowWedTemp',
    label: 'Wed',
  },
  {
    code: 'isDowThurTemp',
    label: 'Thur',
  },
  {
    code: 'isDowFriTemp',
    label: 'Fri',
  },
  {
    code: 'isDowSatTemp',
    label: 'Sat',
  },
];

export const labelsObject = {
  'Poor Grammar': false,
  Typos: false,
  'Great Resume': false,
  'Under Consideration': false,
  Incomplete: false,
  Inexperienced: false,
  'Personality Mismatch': false,
  Unprofessional: false,
  'Wrong Position': false,
  'Insufficient Skills': false,
  'Missing Picture': false,
};

export const computeAction = (label) => {
  switch (label) {
    case 'Missing Picture':
      return { action: 'Add a Profile Photo', href: '/app/dashboard' };
    case 'Insufficient Skills':
      return { action: 'Add missing skills', href: '/app/assessments' };
    case 'Unprofessional':
      return {
        action: 'Review resume for unprofessionalism',
        href: '/app/dashboard/profile',
      };
    case 'Inexperienced':
      return { action: 'Add work history', href: '/app/dashboard/profile' };
    case 'Incomplete':
      return {
        action: 'Complete your Resume Profile',
        href: '/app/dashboard/profile',
      };
    case 'Typos':
      return {
        action: 'Find and fix resume profile typos',
        href: '/app/dashboard/profile',
      };
    case 'Poor Grammar':
      return {
        action: 'Correct Resume Profile grammar',
        href: '/app/dashboard/profile',
      };
    default:
      return { action: 'No action required', href: 'None' };
  }
};
